import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { CheckCircle2, Facebook, Globe2, LockKeyhole, Star } from "lucide-react";
import { useLocation } from "react-router-dom";
import { qualityForPath } from "../data/businessQuality";
import { slugForConceptPath } from "../hooks/useConceptLifecycleStatus";
import {
  backendConfigured,
  fetchBusinessReviews,
  fetchReviewIntegrations,
  submitBusinessReview,
} from "../lib/api";

const blankForm = { author_name: "", email: "", rating: 5, comment: "", consent: false, website: "" };

function ProviderStatus({ icon: Icon, provider }) {
  return (
    <article className={`msc-review-provider ${provider.connected ? "is-connected" : ""}`}>
      <Icon size={19} aria-hidden="true" />
      <div>
        <strong>{provider.label}</strong>
        <span>{provider.connected ? "Connected" : provider.available ? "Ready to connect" : "Connection dependent"}</span>
        <p>{provider.note}</p>
      </div>
    </article>
  );
}

export default function BusinessReviewHub() {
  const { pathname } = useLocation();
  const business = useMemo(() => qualityForPath(pathname), [pathname]);
  // The reviews/review-integrations API is keyed by the business's own base slug (one
  // review profile per firm), not per sub-page — qualityForPath(pathname).path returns
  // the exact sub-page path for multi-page firms (e.g. ".../about"), which would 404
  // against /api/businesses/{slug}/reviews. slugForConceptPath resolves any sub-page
  // back to its parent business's real slug, the same way ConceptLifecycleGate does.
  const slug = useMemo(() => slugForConceptPath(pathname), [pathname]);
  const tagalog = window.localStorage.getItem("mysitecare-language") === "tl";
  const [mount, setMount] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [integrations, setIntegrations] = useState(null);
  const [form, setForm] = useState(blankForm);
  const [state, setState] = useState({ sending: false, message: "", error: false });

  useEffect(() => {
    if (!business) return undefined;
    const footers = document.querySelectorAll("footer");
    const footer = footers[footers.length - 1];
    if (!footer?.parentNode) return undefined;
    const node = document.createElement("div");
    node.dataset.mscReviewHub = business.path;
    footer.parentNode.insertBefore(node, footer);
    setMount(node);
    return () => {
      node.remove();
      setMount(null);
    };
  }, [pathname, business]);

  useEffect(() => {
    let active = true;
    setReviews([]);
    setIntegrations(null);
    setForm(blankForm);
    setState({ sending: false, message: "", error: false });
    if (!slug || !backendConfigured) return () => { active = false; };

    Promise.allSettled([fetchBusinessReviews(slug), fetchReviewIntegrations(slug)]).then(([reviewResult, integrationResult]) => {
      if (!active) return;
      if (reviewResult.status === "fulfilled") setReviews(reviewResult.value);
      if (integrationResult.status === "fulfilled") setIntegrations(integrationResult.value);
    });
    return () => { active = false; };
  }, [slug]);

  if (!business || !mount) return null;

  const live = Boolean(integrations?.website?.connected);
  const providers = integrations || {
    website: { label: "Website reviews", available: true, connected: false, note: "Activates after the business approves this concept site." },
    google: { label: "Google Business Profile", available: true, connected: false, note: "Requires authorization from the verified profile owner or manager." },
    facebook: { label: "Facebook recommendations", available: false, connected: false, note: "Depends on Page-specific Meta permissions and API availability." },
  };

  const submit = async event => {
    event.preventDefault();
    if (!live || state.sending) return;
    setState({ sending: true, message: "", error: false });
    try {
      const receipt = await submitBusinessReview(slug, { ...form, email: form.email || null });
      setForm(blankForm);
      setState({ sending: false, message: receipt.message, error: false });
    } catch (error) {
      setState({
        sending: false,
        message: error.response?.data?.detail || "The review could not be submitted. Please try again later.",
        error: true,
      });
    }
  };

  return createPortal(
    <section className="msc-review-hub notranslate" translate="no" aria-labelledby="msc-review-title">
      <div className="msc-review-hub__inner">
        <div className="msc-review-hub__heading">
          <div>
            <p className="msc-review-hub__eyebrow">{tagalog ? "Mga review ng customer" : "Customer reviews"}</p>
            <h2 id="msc-review-title">{tagalog ? `Ibahagi ang karanasan mo sa ${business.name}` : `Share your experience with ${business.name}`}</h2>
          </div>
          <p>{tagalog ? "Ang mga review mula sa website ay sinusuri muna bago ilathala. Hindi awtomatikong inilalathala ang anumang submission." : "Website reviews are checked before publication. No submission is published automatically."}</p>
        </div>

        <div className="msc-review-provider-grid" aria-label="Review connection status">
          <ProviderStatus icon={Globe2} provider={providers.website} />
          <ProviderStatus icon={CheckCircle2} provider={providers.google} />
          <ProviderStatus icon={Facebook} provider={providers.facebook} />
        </div>

        {reviews.length > 0 && (
          <div className="msc-community-reviews" aria-label="Approved customer reviews">
            {reviews.map(review => (
              <article key={`${review.source}-${review.id || review.author_name}-${review.created_at}`}>
                <div className="msc-community-reviews__stars" aria-label={`${review.rating} out of 5 stars`}>
                  {Array.from({ length: 5 }, (_, index) => <Star key={index} size={14} fill={index < review.rating ? "currentColor" : "none"} />)}
                </div>
                <p>{review.comment || "Rating submitted without a written comment."}</p>
                <footer><strong>{review.author_name}</strong><span>{review.source_label}</span></footer>
              </article>
            ))}
          </div>
        )}

        <div className="msc-review-form-wrap">
          <div className="msc-review-form-copy">
            <LockKeyhole size={22} aria-hidden="true" />
            <h3>{live ? (tagalog ? "Mag-iwan ng review" : "Leave a review") : (tagalog ? "Preview ng review system" : "Review-system preview")}</h3>
            <p>{live
              ? (tagalog ? "Ang email ay opsyonal at hindi kailanman ipinapakita sa publiko. Ang review ay ilalathala lamang pagkatapos ng moderation." : "Email is optional and never displayed publicly. Your review appears only after moderation.")
              : (tagalog ? "Hindi nangongolekta ng personal na impormasyon ang konseptong site na ito. Magiging aktibo ang form kapag inaprubahan at inilunsad ito ng negosyo." : "This unsolicited concept site does not collect personal information. The form activates only after the business approves and launches it.")}</p>
          </div>

          <form className="msc-review-form" onSubmit={submit}>
            <fieldset disabled={!live || state.sending}>
              <div className="msc-review-form__row">
                <label>{tagalog ? "Pangalan" : "Name"}<input required minLength="2" maxLength="80" value={form.author_name} onChange={event => setForm({ ...form, author_name: event.target.value })} /></label>
                <label>{tagalog ? "Email (opsyonal)" : "Email (optional)"}<input type="email" value={form.email} onChange={event => setForm({ ...form, email: event.target.value })} /></label>
              </div>
              <div className="msc-review-form__rating"><strong>{tagalog ? "Rating" : "Rating"}</strong><span>{[5, 4, 3, 2, 1].map(value => <label key={value}><input type="radio" name="rating" value={value} checked={form.rating === value} onChange={() => setForm({ ...form, rating: value })} /><span>{value} ★</span></label>)}</span></div>
              <label>{tagalog ? "Ang iyong review" : "Your review"}<textarea required minLength="10" maxLength="1500" rows="5" value={form.comment} onChange={event => setForm({ ...form, comment: event.target.value })} /></label>
              <label className="msc-review-form__consent"><input type="checkbox" checked={form.consent} onChange={event => setForm({ ...form, consent: event.target.checked })} required /> <span>{tagalog ? "Sumasang-ayon akong iproseso at ilathala ang review na ito pagkatapos ng moderation." : "I consent to this review being processed and published after moderation."}</span></label>
              <label className="msc-review-form__honeypot" aria-hidden="true">Website<input tabIndex="-1" autoComplete="off" value={form.website} onChange={event => setForm({ ...form, website: event.target.value })} /></label>
              <button type="submit">{state.sending ? "Submitting…" : live ? (tagalog ? "Isumite para sa pagsusuri" : "Submit for moderation") : (tagalog ? "Aktibo pagkatapos ng pag-apruba" : "Activates after business approval")}</button>
            </fieldset>
            {state.message && <p className={state.error ? "is-error" : "is-success"} role="status">{state.message}</p>}
          </form>
        </div>
      </div>
    </section>,
    mount,
  );
}
